import React, { useCallback, useLayoutEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container } from "react-bootstrap";
import { useLoading } from "../../../../shared/hooks/useLoading";

import { Contact } from "../../../../components/page-releated/consulta-previa/contact";
import { PropertiesApiService } from "../../../../shared/services/api/properties-api.service";
import {
    IGrpZoneamento,
    IProperty,
    IPropertyGeom,
} from "../../../../shared/interfaces/IProperties";
import { ILayers } from "../../../../shared/interfaces/ILayers";
import { LayersApiService } from "../../../../shared/services/api/layers-api.service";
import { ParkingLot } from "../../../../components/page-releated/consulta-previa/estacionamento";
import { GlobalStyle } from "../../../../components/page-releated/bci/header/styles";
import { Conclusion } from "../../../../components/page-releated/consulta-previa/conclusion";
import { Zoneamento } from "../../../../components/page-releated/consulta-previa/zoneamento";
import { DisclaimerAraucaria } from "../../../../components/page-releated/consulta-previa/disclaimer/araucaria";
import { AraucariaZonUso } from "../../../../components/page-releated/consulta-previa/zona-uso-ocupacao/araucaria";
import { Croqui } from "../../../../components/page-releated/bci/croquis/map";
import { CPFullMapContainer } from "../../../../components/page-releated/bci/croquis/styles";
import { GuiaAmarelaApiService } from "../../../../shared/services/api/guia-amarela-api.service";
import { AraucariaHeader } from "../../../../components/page-releated/consulta-previa/header/araucaria";
import { Separador } from "../../../../shared/assets/logos";
import { AraucariaCvco } from "../../../../components/page-releated/consulta-previa/cvco";
import { AraucariaAlvaraObra } from "../../../../components/page-releated/consulta-previa/alvara-obra";
import { DivisorPeB } from "../../../../components/page-releated/consulta-previa/divisor";
import { getPaddingBasedOnSize } from "../../../../components/page-releated/consulta-previa/helpers/getPaddingBasedOnSize";

export const AraucariaConsultaPrevia: React.FC = () => {
    const { setLoading } = useLoading();

    const { subscription } = useParams<{
        subscription: string | undefined;
    }>();

    const [property, setProperty] = useState<IProperty>();
    const [propertyGeom, setPropertyGeom] = useState<IPropertyGeom>();
    const [layers, setLayers] = useState<ILayers[]>();
    const [zoneamentos, setZoneamentos] = useState<IGrpZoneamento[]>();

    const filterValidZoneamentos = (list: any[]): IGrpZoneamento[] => {
        const valid = list.filter(
            (z): z is IGrpZoneamento => Boolean(z && z.id && z.guia)
        );
        return valid.filter(
            (zon, index, self) => index === self.findIndex((t) => t.id === zon.id)
        );
    };

    const fetchConsultaPrevia = useCallback(async () => {
        document.title = `CONSULTA PRÉVIA - ARAUCÁRIA - ${subscription}`;
        try {
            setLoading(true);

            if (subscription) {
                if (!window.location.href.includes("guia-amarela")) {
                    const {
                        property: propertyResponse,
                        geom: propertyGeomResponse,
                    } = await PropertiesApiService.getPropertyConsultaPrevia(
                        subscription
                    );
                    const layersResponse = await LayersApiService.getLayers(
                        `where={"cam_ativa_consultaprevia":"true"}&orderBy=ASC:cam_ordem&`
                    );
                    const FirstZoneamento =
                        await PropertiesApiService.getZoneamento(
                            propertyResponse.zoneamento?.zona
                        );
                    const SecondZoneamento =
                        await PropertiesApiService.getZoneamento(
                            propertyResponse.zoneamento?.zona_uso
                        );
                    const filtered = filterValidZoneamentos([FirstZoneamento, SecondZoneamento]);
                    setZoneamentos(filtered);
                    setProperty(propertyResponse);
                    setPropertyGeom(propertyGeomResponse);
                    setLayers(layersResponse.data);
                } else {
                    const {
                        property: propertyResponse,
                        geom: propertyGeomResponse,
                    } = await GuiaAmarelaApiService.getPropertyConsultaPrevia(
                        subscription
                    );
                    const layersResponse =
                        await GuiaAmarelaApiService.getLayers(
                            `where={"cam_ativa_consultaprevia":"true"}&orderBy=ASC:cam_ordem&`
                        );

                    const FirstZoneamento =
                        await GuiaAmarelaApiService.getZoneamento(
                            propertyResponse.zoneamento?.zona
                        );

                    const SecondZoneamento =
                        await GuiaAmarelaApiService.getZoneamento(
                            propertyResponse.zoneamento?.zona_uso
                        );

                    const zonaSecundariaSigla =
                        propertyResponse.zoneamento?.zona_secundaria;

                    const ThirdZoneamento = zonaSecundariaSigla
                        ? await GuiaAmarelaApiService.getZoneamento(
                              zonaSecundariaSigla
                          )
                        : null;

                    const rawList = ThirdZoneamento
                        ? [FirstZoneamento, SecondZoneamento, ThirdZoneamento]
                        : [FirstZoneamento, SecondZoneamento];

                    const filtered = filterValidZoneamentos(rawList);
                    setZoneamentos(filtered);
                    setProperty(propertyResponse);
                    setPropertyGeom(propertyGeomResponse);
                    setLayers(layersResponse.data);
                }
            }
        } finally {
            setLoading(false);
        }
    }, [setLoading, subscription]);

    useLayoutEffect(() => {
        fetchConsultaPrevia();
    }, [fetchConsultaPrevia]);

    const hasValidZoneamentos = zoneamentos && zoneamentos.length > 0;
    const isAppZone =
        property?.zoneamento?.zona === "APP" ||
        property?.zoneamento?.zona_uso === "APP";

    return (
        <Container>
            <GlobalStyle />
            <img src={Separador} alt="separador colorido" className="w-100" />
            <AraucariaHeader subscription={subscription || ""} />
            <img src={Separador} alt="separador colorido" className="w-100" />
            <br />

            <DisclaimerAraucaria property={property} />
            <br />
            <Zoneamento
                propertyInfo={property}
                propertyGeom={propertyGeom}
                layers={layers}
                zoneamentos={zoneamentos}
            />
            <div style={{ pageBreakBefore: "always" }} />
            <AraucariaAlvaraObra alvaras={property?.alvara} />

            <AraucariaCvco cvcos={property?.cvco} />

            <div style={{ pageBreakBefore: "always" }} />
            {hasValidZoneamentos ? (
                <div className="text-center  justify-center">
                    <h3>ZONA DE USO E OCUPAÇÃO</h3>
                    {zoneamentos.map((zon) => (
                        <AraucariaZonUso
                            key={zon.id}
                            questions={zon.questions}
                            zoneamento={zon}
                        />
                    ))}
                </div>
            ) : (
                <div
                    className="text-center my-4 p-4 border rounded"
                    style={{
                        backgroundColor: "#fffdf0",
                        borderColor: "#e0d39e",
                    }}
                >
                    <h4 style={{ color: "#856404", fontWeight: "bold" }}>
                        ZONA DE USO E OCUPAÇÃO DO SOLO
                    </h4>
                    <p style={{ fontSize: "1.05rem", marginTop: "12px", marginBottom: "8px" }}>
                        Não constam parâmetros construtivos regulamentados para a zona cadastrada neste imóvel
                        {property?.zoneamento?.zona ? ` (${property.zoneamento.zona})` : ""}.
                    </p>
                    <p style={{ fontSize: "0.95rem", color: "#6c757d", maxWidth: "800px", margin: "0 auto" }}>
                        {isAppZone
                            ? "O imóvel está registrado sob incidência de Área de Preservação Permanente (APP) ou restrição ambiental, não possuindo parâmetros de edificação emitidos automaticamente. Para orientações e consultas prévias especiais, procure a Secretaria Municipal de Urbanismo."
                            : "Trata-se de área com diretrizes sob consulta especial ou pendente de parametrização no sistema. Em caso de dúvidas sobre viabilidade construtiva, procure a Secretaria Municipal de Urbanismo."}
                    </p>
                </div>
            )}

            <ParkingLot />
            <Contact />
            <Conclusion />
            <DivisorPeB />

            {property && propertyGeom && layers && (
                <>
                    <div
                        className="text-center"
                        style={{ pageBreakBefore: "always" }}
                    >
                        <h3>Imagem do Terreno</h3>
                    </div>
                    <div className="w-full h-full flex items-center justify-center">
                        <CPFullMapContainer>
                            <Croqui
                                layers={layers}
                                propertyGeom={propertyGeom}
                                padding={getPaddingBasedOnSize(
                                    property.areaterreno
                                )}
                            />
                        </CPFullMapContainer>
                    </div>
                </>
            )}
        </Container>
    );
};
